//changed due to package upgrade
import React, { useState, useEffect, useRef } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material';
import "react-quill-new/dist/quill.snow.css";
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useDropzone } from 'react-dropzone';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import styled from '@emotion/styled';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import { StudentAccountInfoMain } from '@projectK/MainComponents';
import { HandleMobileFormat } from '@projectK/ReUseFunctions';
const AvatarViewWrapper = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    cursor: 'pointer',
    '& .profile-edit-icon': {
      position: 'absolute',
      bottom: 0,
      right: 0,
      zIndex: 1,
      border: `solid 2px ${theme.palette.background.paper}`,
      backgroundColor: alpha(theme.palette.primary.main, 0.7),
      color: theme.palette.primary.contrastText,
      borderRadius: '50%',
      width: 26,
      height: 26,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      '& .MuiSvgIcon-root': {
        fontSize: 16,
      },
    },
    '&.dropzone': {
      outline: 0,
      '&:hover .profile-edit-icon, &:focus .profile-edit-icon': {
        display: 'flex',
      },
    },
  };
});

const studentValidationSchema = yup.object({
   first_name: yup.string().nullable().required(<IntlMessages id='validation.firstNameRequired'/>),
    last_name: yup.string().nullable().required(<IntlMessages id='validation.lastNameRequired'/>),
    user_mobile: yup
      .string()
      .matches(
        /^\d{3}-\d{3}-\d{4}$/,
        () => <IntlMessages id='validation.invalidPhoneNumber'/>,
      )
      .nullable()
      .optional(),
    email: yup
      .string()
      .matches(
        /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
        () => <IntlMessages id='validation.invalidEmailFormat'/>,
      )
      .typeError(<IntlMessages id='validation.invalidEmail'/>)
      .required(<IntlMessages id='validation.emailRequired' />),
    parent1_mobile: yup
      .string()
      .matches(
        /^\d{3}-\d{3}-\d{4}$/,
        () => <IntlMessages id='validation.invalidPhoneNumber'/>,
      )
      .nullable()
      .optional(),
    parent2_mobile: yup
      .string()
      .matches(
        /^\d{3}-\d{3}-\d{4}$/,
        () => <IntlMessages id='validation.invalidPhoneNumber'/>,
      )
      .nullable()
      .optional(),
  parent1_name: yup.string().nullable().optional(),
  parent1_last_name: yup
    .string()
    .nullable()
    .optional(),
  parent1_email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      'Invalid parent1 email format.',
    )
    .typeError('Enter a valid email.')
    .nullable()
    .optional(),
  // .required(<IntlMessages id='validation.emailRequired' />),
  parent2_name: yup.string().nullable().optional(),
  parent2_last_name: yup.string().nullable().optional(),
  parent2_email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      'Invalid parent2 email format.',
    )
    .typeError('Enter a valid email.')
    .nullable()
    .optional(),
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong'/>)
    .nullable()
    .optional(),
  parent_address_1: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong'/>)
    .nullable()
    .optional(),
  parent_address_2: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong'/>)
    .nullable()
    .optional(),
    zip_code: yup
    .string()
    .nullable()
    .optional()
    .matches(
      /^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/,
      () => <IntlMessages id='validation.zipCodeFormat' />
    ),
  parent1_zipcode: yup
    .string()
    .matches(
      /^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/,
      () => <IntlMessages id='validation.zipCodeFormat' />
    )
    .nullable()
    .optional(),
  parent2_zipcode: yup
    .string()
    .matches(
      /^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/,
      () => <IntlMessages id='validation.zipCodeFormat' />
    )
    .nullable()
    .optional(),
   jersey_size: yup
     .string()
     .matches(/^[A-Za-z0-9]+$/, () => <IntlMessages id='validation.invalidJerseySize'/>)
     .nullable()
     .optional(),
 act_score: yup
    .number()
    .typeError(<IntlMessages id='validation.invalidActScore'/>)
    .nullable()
    .optional()
    .min(1, () => <IntlMessages id='validation.actScoreTooLow'/>)
    .max(36, () => <IntlMessages id='validation.actScoreTooHigh'/>),
 gpa: yup
     .number()
     .nullable()
     .optional()
     .min(0, () => <IntlMessages id='validation.gpaTooLow'/>)
     .max(5.0, () => <IntlMessages id='validation.gpaTooHigh'/>)
     .positive(<IntlMessages id='validation.gpaNotPositive'/>)
     .typeError(<IntlMessages id='validation.invalidGpa'/>),
  emergency_contact_name: yup.string().nullable().optional(),
  emergency_contact_number: yup
    .string()
    .nullable()
    .optional()
    .matches(
      /^\d{3}-\d{3}-\d{4}$/,
      () => <IntlMessages id='validation.invalidPhoneNumber'/>,
    ),
  relation: yup.string().nullable().optional(),
  extented_grace_period: yup
    .number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === '' ? null : value
    )
    .nullable()
    .typeError('Grace Period must be a number.')
    .min(0, 'Grace Period cannot be negative.'),

});
const colorValidationSchema = yup.object({
  calendar_color_code: yup.string().required(<IntlMessages id='validation.colorCode'/>),
});
const getCorrectValues = ({ values, rest }) => {
  const normalize = (val) => (val === '' ? null : val);

  const fieldsToNormalize = [
    'user_mobile',
    'parent1_name',
    'parent1_last_name',
    'parent1_mobile',
    'parent2_name',
    'parent2_last_name',
    'parent1_email',
    'parent2_mobile',
    'address',
    'jersey_size',
    'act_score',
    'gpa',
    'zip_code',
    'emergency_contact_number',
    'emergency_contact_name',
    'relation',
    'campus_id',
    'calendar_color_code',
    'extented_grace_period',
  ];

  const normalizedValues = fieldsToNormalize.reduce((acc, field) => {
    acc[field] = normalize(values?.[field]);
    return acc;
  }, {});

  return {
    ...normalizedValues,
    ...rest,
  };
};
const getBioContent = ({ settings, isFullBioVisible }) => {
  let bioContent;
  if (settings?.biodata) {
    if (isFullBioVisible) {
      bioContent = settings.biodata;
    } else {
      bioContent = settings.biodata.slice(0, 200);
    }
  }
  return bioContent;
};
const getInitialValues = ({ settings = {} }) => {
  const {
    first_name = '',
    last_name = '',
    email = '',
    user_mobile,
    roles = [],
    user_profile_pic = '',
    biodata = '',
    parent1_name = '',
    parent1_last_name = null,
    parent1_email = null,
    parent_address_1 = null,
    parent1_zipcode = null,
    parent1_mobile,
    parent2_name = '',
    parent2_last_name = '',
    parent2_email = null,
    parent_address_2 = null,
    parent2_zipcode = null,
    parent2_mobile,
    address = '',
    zip_code,
    jersey_size = '',
    act_score = '',
    gpa,
    emergency_contact_number,
    emergency_contact_name = '',
    relation = '',
    campus_id = null,
    calendar_color_code = '',
    extented_grace_period,
  } = settings;

  return {
    first_name,
    last_name,
    email,
    user_mobile: HandleMobileFormat(user_mobile, true) || '',
    roleName: roles[0]?.name || '',
    user_profile_pic,
    biodata,
    parent1_name,
    parent1_last_name,
    parent1_email,
    parent_address_1,
    parent1_zipcode,
    parent1_mobile: HandleMobileFormat(parent1_mobile, true) || '',
    parent2_name,
    parent2_last_name,
    parent2_email,
    parent_address_2,
    parent2_zipcode,
    parent2_mobile: HandleMobileFormat(parent2_mobile, true) || '',
    address,
    zip_code: zip_code?.toString() || '',
    jersey_size,
    act_score,
    gpa: gpa?.toString() || '',
    emergency_contact_number:
      HandleMobileFormat(emergency_contact_number, true) || '',
    emergency_contact_name,
    relation,
    campus_id,
    calendar_color_code,
    extented_grace_period: extented_grace_period || null,
  };
};
const StudentAccountInfo = ({ settings, fetchData, roleData, hasPermission, rolePermissions }) => {
  const companyTheme = CompanyTheme();
  const { isMobile } = useMobileSettings();
  const infoViewActionsContext = useInfoViewActionsContext();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [color, setColor] = useState(settings?.calendar_color_code || '');

  const dropzone = useDropzone({
    accept: {
      'image/png': ['.png', '.jpeg', '.jpg'],
    },
    onDrop: (acceptedFiles) => {
      // Append new files to the existing uploadedFiles array
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      ]);
    },
  });
  useEffect(() => {
    setUploadedFiles(dropzone.acceptedFiles);
  }, [dropzone.acceptedFiles]);

  const { user } = useAuthUser();
  const [isFullBioVisible, setIsFullBioVisible] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const pickerRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    }
    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);
  const handleReadMore = () => {
    setIsFullBioVisible(true);
  };

  const handleReadLess = () => {
    setIsFullBioVisible(false);
  };
  let bioContent = getBioContent({ isFullBioVisible, settings });

  const handleMobileFormat = (event, name) => {
    let inputValue = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters
    let formattedValue = '';

    // Ensure the first group of digits is not longer than 3 digits
    if (inputValue.length > 3) {
      inputValue = inputValue.slice(0, 3) + '-' + inputValue.slice(3);
    }

    // Ensure the second group of digits is not longer than 6 digits
    if (inputValue.length > 7) {
      inputValue = inputValue.slice(0, 7) + '-' + inputValue.slice(7);
    }

    // Ensure the total length does not exceed 12 characters
    formattedValue = inputValue;

    // Update form field value
    event.target.value = formattedValue; // Set input value to formatted value

    if (name === 'user_mobile') {
      return { name: 'user_mobile', value: formattedValue || null };
    } else if (name === 'parent1_mobile') {
      return { name: 'parent1_mobile', value: formattedValue || null };
    } else if (name === 'parent2_mobile') {
      return { name: 'parent2_mobile', value: formattedValue || null };
    } else if (name === 'emergency_contact_number') {
      return {
        name: 'emergency_contact_number',
        value: formattedValue || null,
      };
    }
  };
  const handleFocus = () => {
    setShowColorPicker(true); // Show the color picker when the input field is focused
  };

  const handleColorBlur = (event) => {
    setShowColorPicker(false);
  };
  const validationSchema = (userData) => {
    if (userData.roleMenuFlag[0] === 'parent' && userData.actualRoleMenuFlag[0] === 'parent') {
      return studentValidationSchema.concat(colorValidationSchema);
    } else {
      return studentValidationSchema;
    }
  };
  return (
    <StudentAccountInfoMain
      fetchData={fetchData}
      AvatarViewWrapper={AvatarViewWrapper}
      dropzone={dropzone}
      uploadedFiles={uploadedFiles}
      user={user}
      isFullBioVisible={isFullBioVisible}
      handleReadLess={handleReadLess}
      handleReadMore={handleReadMore}
      companyTheme={companyTheme}
      isMobile={isMobile}
      validationSchema={validationSchema}
      handleFocus={handleFocus}
      handleColorBlur={handleColorBlur}
      setShowColorPicker={setShowColorPicker}
      showColorPicker={showColorPicker}
      handleMobileFormat={handleMobileFormat}
      setColor={setColor}
      color={color}
      bioContent={bioContent}
      settings={settings}
      infoViewActionsContext={infoViewActionsContext}
      getInitialValues={getInitialValues}
      getCorrectValues={getCorrectValues}
      pickerRef={pickerRef}
      roleData={roleData}
      rolePermissions={rolePermissions}
      hasPermission={hasPermission}
    />
  );
};

export default StudentAccountInfo;

StudentAccountInfo.propTypes = {
  fetchData: PropTypes.func,
  settings: PropTypes.object,
  roleData: PropTypes.object,
  rolePermissions: PropTypes.object,
  hasPermission: PropTypes.func,
};
